import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { LandingHeader } from "@/components/landing-header";
import { TrustStrip } from "@/components/trust-strip";
import { VslPlayer } from "@/components/vsl-player";
import {
  ScheduleLeadForm,
  normalizeUrl,
  type FormStep,
  type LeadContact,
} from "@/components/schedule-lead-form";
import { attachCalendlyTracking } from "@/lib/analytics/calendly-tracking";
import { useCalendlyPopup } from "@/hooks/use-calendly-popup";
import { getCalendlyInvitee } from "@/lib/calendly.functions";
import { upsertLoopsContact } from "@/lib/loops.functions";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

/** Shared `head()` builder for the schedule landing pages. */
export function scheduleSeoHead({
  path,
  title,
  description,
  noindex,
  canonical,
}: {
  path: string;
  title: string;
  description: string;
  /**
   * Set on A/B test arms. Two near-identical ad landing pages are textbook
   * duplicate content, and only the control needs to be indexable.
   */
  noindex?: boolean;
  /**
   * Absolute canonical override. A test variant points this at the control URL
   * so the two arms consolidate to one page for search engines.
   */
  canonical?: string;
}) {
  return seo({
    title,
    description,
    path,
    type: "website",
    noindex,
    canonical,
    jsonLd: [
      webPage({ name: title, description, url: `${SITE_URL}${path}` }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: title, url: path },
      ]),
    ],
  });
}

/** Deterministic same-origin Worker media paths for a given VSL media id. */
export function vslMediaConfig(mediaId: string) {
  return {
    mediaId,
    masterUrl: `/media/${mediaId}/master.m3u8`,
    posterUrl: `/media/${mediaId}/poster.webp`,
  };
}

/** Copy for the offer card below the video. */
export type ScheduleOffer = {
  /** Optional heading above the paragraphs. */
  title?: string;
  paragraphs: string[];
  /** Optional lead-in for `items` (e.g. "What you'll get on the call:"). */
  listTitle?: string;
  /** Optional numbered list rendered under `listTitle`. */
  items?: { title: string; description: string }[];
};

/**
 * Video-first "watch then book" landing page. Identical UI/UX across variants —
 * only the copy and the Calendly event type change.
 *
 * The CTA opens Calendly directly. Booking no longer gates the page: once the
 * visitor books and closes the Calendly popup, a post-booking qualification
 * wizard opens and progressively saves answers to Loops.
 */
export function ScheduleLandingPage({
  title,
  subtitle,
  ctaNotes,
  offer,
  trustLabel,
  video,
  calendlyUrl,
  formName,
  steps,
}: {
  title: string;
  /** Optional deck between the H1 and the video. */
  subtitle?: string;
  /** Social proof / microcopy lines under the primary CTA. */
  ctaNotes?: string[];
  offer: ScheduleOffer;
  trustLabel: string;
  video: { mediaId: string; masterUrl: string; posterUrl: string };
  /**
   * Required, not defaulted: each schedule variant books a distinct Calendly
   * event type, and a silent fallback would send agency leads to the wrong one.
   */
  calendlyUrl: string;
  /** Recorded to Loops so post-booking answers attribute to this page. */
  formName: string;
  /** Qualifying steps shown by the post-booking wizard. */
  steps: FormStep[];
}) {
  const openCalendly = useCalendlyPopup();
  const [bookingContact, setBookingContact] = useState<LeadContact | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    // Guards a single lookup per mount: `event_scheduled` can fire more than once.
    let booked = false;
    let overlayObserver: MutationObserver | null = null;

    /**
     * The wizard must not cover Calendly's confirmation screen, so it opens only
     * once the popup overlay leaves the DOM. If no overlay exists at booking
     * time, the widget fell back to a new tab — our page is already in front of
     * the visitor, so open immediately.
     */
    function openFormWhenPopupCloses() {
      if (!document.querySelector(".calendly-overlay")) {
        setFormOpen(true);
        return;
      }
      overlayObserver = new MutationObserver(() => {
        if (!document.querySelector(".calendly-overlay")) {
          setFormOpen(true);
          overlayObserver?.disconnect();
          overlayObserver = null;
        }
      });
      overlayObserver.observe(document.body, { childList: true });
    }

    const detach = attachCalendlyTracking({
      mediaId: video.mediaId,
      onEventScheduled: ({ inviteeUri }) => {
        if (booked) return;
        booked = true;
        if (inviteeUri) {
          getCalendlyInvitee({ data: { inviteeUri } })
            .then((resolved) => {
              if (!resolved) return;
              setBookingContact(resolved);
              // Best-effort: land every booking in Loops even if the wizard is dismissed.
              upsertLoopsContact({
                data: {
                  email: resolved.email,
                  firstName: resolved.firstName,
                  lastName: resolved.lastName,
                  companyWebsite: resolved.website ? normalizeUrl(resolved.website) : undefined,
                  formName,
                },
              }).catch(() => {});
            })
            .catch(() => {});
        }
        openFormWhenPopupCloses();
      },
    });

    return () => {
      detach();
      overlayObserver?.disconnect();
    };
  }, [video.mediaId, formName]);

  return (
    <>
      <LandingHeader showCta={false} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-16 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <h1
            className={[
              "text-2xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance",
              subtitle ? "mb-5" : "mb-10",
            ].join(" ")}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
              {subtitle}
            </p>
          )}

          {/* VIDEO */}
          <div className="relative mx-auto max-w-3xl">
            <div className="glass-card overflow-hidden rounded-2xl p-2">
              <VslPlayer
                mediaId={video.mediaId}
                masterUrl={video.masterUrl}
                posterUrl={video.posterUrl}
                className="rounded-xl bg-neutral-900 overflow-hidden"
              />
            </div>
          </div>

          {/* PRIMARY CTA */}
          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => openCalendly(calendlyUrl)}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-7 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Book a call
              <ArrowRight className="size-4" />
            </button>
            {ctaNotes?.map((note, i) => (
              <p
                key={note}
                className={
                  i === 0 ? "text-sm text-white/70" : "text-xs text-white/50 max-w-md text-pretty"
                }
              >
                {note}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* OFFER */}
      <section className="px-6 pb-10 pt-10">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card p-8 md:p-10">
            {offer.title && (
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-white text-balance mb-4 whitespace-pre-line">
                {offer.title === "Schedule Demo &\u00a0\nGet a Free AI Visibility Audit" ? (
                  <>
                    Schedule Demo &{"\u00a0"}
                    <br />
                    Get a Free AI Visibility Audit
                  </>
                ) : (
                  offer.title
                )}
              </h2>
            )}
            {offer.paragraphs.map((para, i) => (
              <p
                key={para}
                className={[
                  "text-white/80 leading-relaxed text-pretty whitespace-pre-line",
                  i < offer.paragraphs.length - 1 ? "mb-5" : "",
                ].join(" ")}
              >
                {para}
              </p>
            ))}
            {offer.items && offer.items.length > 0 && (
              <>
                {offer.listTitle && (
                  <p className="mt-7 mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-pink-soft/90">
                    {offer.listTitle}
                  </p>
                )}
                <ol className="flex flex-col gap-4">
                  {offer.items.map((item, i) => (
                    <li key={item.title} className="flex gap-4">
                      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-pink-soft/10 text-xs font-semibold text-pink-soft ring-1 ring-pink-soft/30">
                        {i + 1}
                      </span>
                      <span className="text-white/80 leading-relaxed text-pretty">
                        <span className="font-medium text-white">{item.title}:</span>{" "}
                        {item.description}
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
      </section>

      {/* LOGO WALL */}
      <TrustStrip label={trustLabel} />

      <p className="text-xs md:text-sm text-white/50 text-center max-w-3xl mx-auto -mt-6 mb-10 px-6">
        *terms and conditions apply
      </p>

      <ScheduleLeadForm
        open={formOpen}
        onOpenChange={setFormOpen}
        formName={formName}
        steps={steps}
        contact={bookingContact}
      />
    </>
  );
}
